import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { Send, Heart } from 'lucide-react';
import { BDAY_EASE } from '../theme';
import type { BirthdayContent, BirthdayWish } from '../content';
import { getBirthdayWishes, postBirthdayWish } from '@/src/infrastructure/lib/api';

// Sample wishes shown only in the wizard preview / draft (no live invitation).
// On a published invitation the wall is populated from real guest submissions.
const SAMPLE_WISHES: BirthdayWish[] = [
  { name: 'Uncle Raj', message: 'Wishing you all the joy and happiness today and always! 🎉' },
  { name: 'Aunt Neha', message: 'May this year bring you everything your heart desires. Love you lots! ❤️' },
  { name: 'Priya & Family', message: 'Happy birthday to our favourite star! Keep shining bright ✨' },
];

function WishCard({ wish, index }: { wish: BirthdayWish; index: number }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      transition={{ duration: 0.5, delay: index * 0.08, ease: BDAY_EASE }}
      className="bg-white/5 backdrop-blur-sm border border-blue-400/20 rounded-2xl p-5 flex flex-col gap-3"
    >
      <p className="text-blue-100 text-sm leading-relaxed italic">"{wish.message}"</p>
      <div className="flex items-center gap-2 mt-auto">
        <div className="w-7 h-7 rounded-full bg-blue-500/30 border border-blue-400/30 flex items-center justify-center text-sm">
          {wish.name.charAt(0).toUpperCase()}
        </div>
        <p className="text-blue-300 text-xs font-semibold">— {wish.name}</p>
      </div>
    </motion.div>
  );
}

interface WishesWallSectionProps {
  content: BirthdayContent;
  /** Live invitation id — present on published/draft invitations, null in preview. */
  invitationId?: string | null;
  /** True only when the invitation is published/processing (accepts real submissions). */
  isLive?: boolean;
}

export function WishesWallSection({ content, invitationId, isLive }: WishesWallSectionProps) {
  // Live wall = real guest submissions from the API. Otherwise seed from the
  // host-authored wishes, falling back to samples for the preview.
  const seeded = content.wishes.length ? content.wishes : SAMPLE_WISHES;
  const [wishes, setWishes] = useState<BirthdayWish[]>(isLive ? [] : seeded);
  const [name, setName] = useState('');
  const [message, setMessage] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const live = Boolean(isLive && invitationId);

  // Load the real wall once for a published invitation.
  useEffect(() => {
    if (!live || !invitationId) return;
    let cancelled = false;
    getBirthdayWishes(invitationId)
      .then((rows) => {
        if (cancelled) return;
        setWishes(rows.map((r) => ({ name: r.guestName, message: r.message, createdAt: r.createdAt })));
      })
      .catch(() => { /* empty wall on failure — non-blocking */ });
    return () => { cancelled = true; };
  }, [live, invitationId]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim() || !message.trim() || sending) return;
    setError(null);

    // Preview / draft: optimistic local-only add (no network).
    if (!live || !invitationId) {
      setWishes((prev) => [{ name: name.trim(), message: message.trim(), createdAt: new Date().toISOString() }, ...prev]);
      setName('');
      setMessage('');
      setSubmitted(true);
      setTimeout(() => setSubmitted(false), 3000);
      return;
    }

    // Published: persist through the API, then show the server's copy.
    setSending(true);
    try {
      const saved = await postBirthdayWish(invitationId, { guestName: name.trim(), message: message.trim() });
      setWishes((prev) => [{ name: saved.guestName, message: saved.message, createdAt: saved.createdAt }, ...prev]);
      setName('');
      setMessage('');
      setSubmitted(true);
      setTimeout(() => setSubmitted(false), 3000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not send your wish. Please try again.');
    } finally {
      setSending(false);
    }
  };

  const inputClass =
    'w-full rounded-xl border border-blue-400/20 bg-white/5 px-4 py-3 text-white placeholder:text-blue-400/40 outline-none focus:border-blue-400/60 focus:ring-2 focus:ring-blue-400/10 transition-all text-sm';

  return (
    <section
      className="relative py-24 px-6 overflow-hidden"
      style={{ background: 'linear-gradient(180deg, #0f172a 0%, #1e3a5f 100%)' }}
    >
      <div className="max-w-2xl mx-auto">
        {/* heading */}
        <div className="text-center mb-12">
          <motion.p
            initial={{ opacity: 0, y: -10 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, ease: BDAY_EASE }}
            className="text-blue-400 text-xs font-bold uppercase tracking-[0.25em] mb-2"
          >
            💌 Guest Messages
          </motion.p>
          <motion.h2
            initial={{ opacity: 0, y: 12 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.1, ease: BDAY_EASE }}
            className="text-3xl sm:text-4xl font-bold text-white"
          >
            Birthday Wishes Wall
          </motion.h2>
          <motion.p
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="mt-3 text-blue-300 text-sm"
          >
            Leave your love for the birthday star
          </motion.p>
        </div>

        {/* wish form */}
        <motion.form
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, ease: BDAY_EASE }}
          onSubmit={handleSubmit}
          className="bg-white/5 backdrop-blur-sm border border-blue-400/20 rounded-3xl p-6 mb-8 flex flex-col gap-4"
        >
          <input
            className={inputClass}
            placeholder="Your name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            maxLength={60}
            required
          />
          <textarea
            className={`${inputClass} resize-none`}
            placeholder="Write your birthday wish..."
            rows={3}
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            maxLength={280}
            required
          />
          <div className="flex items-center justify-between gap-4">
            <AnimatePresence>
              {submitted && (
                <motion.p
                  initial={{ opacity: 0, x: -10 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0 }}
                  className="text-blue-300 text-sm flex items-center gap-1"
                >
                  <Heart size={13} className="fill-current" /> Wish sent!
                </motion.p>
              )}
            </AnimatePresence>
            <button
              type="submit"
              disabled={sending}
              className="ml-auto inline-flex items-center gap-2 px-6 py-2.5 bg-gradient-to-r from-blue-500 to-blue-700 text-white font-bold text-sm rounded-2xl hover:from-blue-400 hover:to-blue-600 transition-all shadow-lg disabled:cursor-not-allowed disabled:opacity-60"
            >
              <Send size={14} /> {sending ? 'Sending…' : 'Send Wish'}
            </button>
          </div>
          {error && <p className="text-red-300 text-xs font-semibold">{error}</p>}
        </motion.form>

        {/* wishes grid */}
        <div className="grid sm:grid-cols-2 gap-4">
          <AnimatePresence mode="popLayout">
            {wishes.map((wish, i) => (
              <WishCard key={`${wish.name}-${i}`} wish={wish} index={i} />
            ))}
          </AnimatePresence>
        </div>
      </div>
    </section>
  );
}
